import React from 'react';
import { Button, Space, Switch, Typography, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SlotUnavailableViolation } from '../types';

interface FormActionsProps {
  onCancel: () => void;
  isSubmitting: boolean;
  showConfirmSwitch?: boolean;
  createAsConfirmed: boolean;
  onCreateAsConfirmedChange: (value: boolean) => void;
  isCheckingSlots: boolean;
  slotUnavailable: SlotUnavailableViolation | null;
  truncated: boolean;
  hasCommittedStartTime: boolean;
}

/** Dialog footer: optional "create as confirmed" switch on the left, Cancel + submit on the right. */
export const FormActions: React.FC<FormActionsProps> = ({
  onCancel,
  isSubmitting,
  showConfirmSwitch,
  createAsConfirmed,
  onCreateAsConfirmedChange,
  isCheckingSlots,
  slotUnavailable,
  truncated,
  hasCommittedStartTime
}) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  const isDisabled =
    !hasCommittedStartTime ||
    slotUnavailable?.hasViolation === true ||
    truncated ||
    (hasCommittedStartTime && isCheckingSlots);

  const isLoading = isSubmitting || isCheckingSlots;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: token.marginSM,
        marginTop: token.marginSM,
        paddingTop: token.paddingMD,
        borderTop: `1px solid ${token.colorBorderSecondary}`
      }}
    >
      <div>
        {showConfirmSwitch && (
          <Space size="small">
            <Switch
              size="small"
              checked={createAsConfirmed}
              onChange={onCreateAsConfirmedChange}
              disabled={isSubmitting || isLoading}
            />
            <Typography.Text>
              {t('appointmentComponents.create.actions.createConfirmed')}
            </Typography.Text>
          </Space>
        )}
      </div>
      <div style={{ marginLeft: 'auto' }}>
        <Space size={token.marginSM} wrap>
          <Button onClick={onCancel} disabled={isSubmitting}>
            {t('appointmentComponents.create.actions.cancel')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={isDisabled}
            icon={!isLoading ? <PlusOutlined /> : undefined}
          >
            {isSubmitting
              ? t('appointmentComponents.create.actions.creating')
              : isCheckingSlots
                ? t('appointmentComponents.create.actions.checkingAvailability')
                : t('appointmentComponents.create.actions.createAppointment')}
          </Button>
        </Space>
      </div>
    </div>
  );
};
